import { ButtonCategory } from "@components/atoms/Button/types";
import { SectionLayout, Shadow } from "@utils/customization";
import Routes from "@navigations/Routes";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { hexToRgba } from "@utils/design-tokens";
import { Linking, Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { RequestType } from "@scenes/bulk-request/Utils";
import { SearchFilterTypeEnumLowercase } from "@components/atoms/Filters/utils";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import * as Clipboard from "expo-clipboard";
import { ProductCategory } from "@utils/constants";
import { parseAndNavigate } from "./searchResultsNavigation";
import { EventType } from "@utils/analytics";
import { BannerActionEnum } from "@components/molecules/AdView/BannerAd/types";
import { createAdProps } from "@components/molecules/AdView/BannerAd/utils";

const PERSISTENCE_KEY = "NAVIGATION_STATE";
const HISTORY_KEY = "history";
const sessStorage = Platform.OS === "web" ? sessionStorage : AsyncStorage;

const isDiamondOrLabGrown = (productType?: string) => {
  return (
    productType === ProductCategory.LAB_GROWN_DIAMOND ||
    productType === ProductCategory.DIAMOND
  );
};

const isGemstone = (productType?: string) => {
  return productType === ProductCategory.GEMSTONE;
};

const isJewelry = (productType?: string) => {
  return productType === ProductCategory.JEWELRY;
};

const isDiamond = (productType?: string) => {
  return productType === ProductCategory.DIAMOND;
};

const isLabGrown = (productType?: string) => {
  return productType === ProductCategory.LAB_GROWN_DIAMOND;
};

const isLabGrownJewelry = (productType?: string) => {
  return productType === ProductCategory.LABGROWN_JEWELRY;
};

export const capitalizeWords = (text: string): string => {
  return text
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getSubtitlePriceModes = (
  itemDetailsCardSubSections: any[] | null
): number[] | null => {
  if (
    !itemDetailsCardSubSections ||
    !Array.isArray(itemDetailsCardSubSections)
  ) {
    return null;
  }

  const subTitleSection = itemDetailsCardSubSections.find(
    (subSection: any) => subSection.name === "sub-title"
  );

  if (
    subTitleSection &&
    subTitleSection.price_modes &&
    Array.isArray(subTitleSection.price_modes)
  ) {
    return subTitleSection.price_modes;
  }

  return null;
};

const getButtonCategory = (productType?: string): ButtonCategory => {
  switch (productType) {
    case ProductCategory.DIAMOND:
      return ButtonCategory.diam;
    case ProductCategory.GEMSTONE:
      return ButtonCategory.gem;
    case ProductCategory.JEWELRY:
      return ButtonCategory.jewelry;
    case ProductCategory.LAB_GROWN_DIAMOND:
      return ButtonCategory.lab;
    case ProductCategory.LABGROWN_JEWELRY:
      return ButtonCategory.jewelry;
    default:
      return ButtonCategory.default;
  }
};

const getFilterDetailData = (
  searchText: string,
  sectionLayout: SectionLayout
): any[] => {
  if (!searchText || !sectionLayout?.sub_section) {
    return [];
  }

  const subSections = Object.values(sectionLayout.sub_section);

  if (!Array.isArray(subSections)) {
    return [];
  }

  return subSections.filter((item) => item.name === searchText);
};

var isValidDate = function (date: string) {
  return new Date(date) !== "Invalid Date" && !isNaN(new Date(date));
};

const getScreenContent = (response) => {
  if (!response?.success) {
    return {
      title: "unverified.title.text",
      description: "unverified.description.text",
      buttonText: "unverified.button.text",
    };
  }

  if (response.success && response.user_active === false) {
    return {
      title: "inactive.title.text",
      description: "inactive.description.text",
      buttonText: "inactive.button.text",
    };
  }

  return {
    title: "verified.title.text",
    description: "verified.description.text",
    buttonText: "verified.button.text",
  };
};

const handleSupplierForPaUser = (
  deepLink: string,
  navigate: any,
  productPrefix: string
) => {
  const extractedParams = extractParamsFromURL(deepLink);

  navigate(Routes.GlobalNavigator, {
    screen: Routes.PDPScreen,
    params: {
      productID: extractedParams?.itemId,
      productType: extractedParams?.product,
      externalPriceMode:
        !isNaN(Number(extractedParams?.priceMode)) &&
        Number(extractedParams?.priceMode) > -1
          ? Number(extractedParams?.priceMode)
          : PriceModes.WHITE,
      fromRequestDetailsScreen: true,
      productPrefix,
    },
  });
};

const extractParamsFromURL = (url) => {
  if (url.includes("webapp/")) {
    const queryString = url?.split("?")?.[1];

    const queryParams = {};

    queryString.split("&").forEach((pair) => {
      const [key, value] = pair.split("=");
      queryParams[key] = decodeURIComponent(value);
    });

    const params = { product: null, priceMode: null, itemId: null };
    const urlObj = new URL(url);

    const pathSegments = urlObj.pathname.split("/");
    const itemId = pathSegments[pathSegments.length - 1];

    if (!!itemId && !!queryParams?.productType && !!queryParams?.priceMode) {
      params.product = queryParams?.productType;
      params.priceMode = queryParams?.priceMode;
      params.itemId = itemId;
    }
    return params;
  } else if (url.includes("vdbapp://")) {
    const path = url.replace("vdbapp://", "");
    const segments = path.split("/");

    if (segments.length < 3) {
      throw new Error("Invalid URL structure");
    }

    const [product, priceMode, , itemId] = segments;

    const params = { product: null, priceMode: null, itemId: null };
    if (!!product && !!priceMode && !!itemId) {
      params.product = product;
      params.priceMode = priceMode;
      params.itemId = itemId;
    }
    return params;
  }
};

const getBoxShadow = (theme: Shadow) => {
  return `${theme.x}px ${theme.y}px ${theme.blur}px rgba(${hexToRgba(
    theme.color
  )})`;
};

const handleAdLinkPress = async (
  link?: string,
  products?: any,
  trackAdEvent?: any,
  item?: any
) => {
  if (link) {
    if (trackAdEvent) {
      trackAdEvent(
        EventType.VIEW_BANNER,
        createAdProps(BannerActionEnum.clickThrough, item, {
          campaign: item.campaign,
        })
      );
    }
    await parseAndNavigate(link, products);
  }
};

const findRoute = ({ state, route }: any) => {
  if (state && state?.routes?.length) {
    const navigator = state.routes.find(
      (_route: any) =>
        _route?.name !== Routes.AppNavigator &&
        (_route?.name === route || _route?.params?.screen === route)
    );
    if (navigator) return navigator;
    for (const _route of state.routes) {
      const nestedState = _route?.state;
      if (nestedState) {
        const found = findRoute({ state: nestedState, route });
        if (found) return found;
      }
    }
  }
};

const getNavigatorFromRoute = async ({ route }: any) => {
  const storage = Platform.OS === "web" ? sessionStorage : AsyncStorage;
  let state: any = await storage.getItem(PERSISTENCE_KEY);
  state = JSON.parse(state ?? null) ?? {};
  return findRoute({ state: state?.routes?.[0]?.state, route });
};

const getSessionHistory = async () => {
  const history = await sessStorage.getItem(HISTORY_KEY);
  return history ? JSON.parse(history) : [];
};

const saveToSessionhistory = async ({ history }: any) => {
  await sessStorage.setItem(HISTORY_KEY, history);
};

const saveHistoryToSessionStorage = async ({ currentRoute }: any) => {
  const navigator = await getNavigatorFromRoute({ route: currentRoute });
  if (navigator && navigator?.name) {
    let history = await getSessionHistory();
    if (Array.isArray(history)) {
      const index = history.indexOf(navigator.name);
      if (index !== -1) {
        history.splice(index, 1);
      }
      history.push(navigator.name);
      await saveToSessionhistory({ history: JSON.stringify(history) });
    }
  }
};

const debounceFunction = (func: any, delay = 1000) => {
  let timer;
  return function (...args) {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => func.apply(this, args), delay);
  };
};

const getGMTTime = () => {
  const now = new Date();
  return now.toUTCString();
};

const resetAdBannerTime = async () => {
  try {
    await AsyncStorage.removeItem("@adBannerTime");
  } catch (error) {
    console.error("Failed to save ad banner time:", error);
  }
};

const getNormalizeRequestType = (type: string, requestType?: RequestType) => {
  const tokens = type.split("_");

  const validTypes = requestType
    ? [requestType]
    : [RequestType.BUY, RequestType.HOLD, RequestType.MEMO, RequestType.QUOTE];

  for (const i of validTypes) {
    if (tokens.includes(i)) {
      return i;
    }
  }
};

const replaceDeeplinkScheme = (url: string, appScheme: string) => {
  if (url && Platform.OS !== "web") {
    const _url = new URL(url);
    return `${appScheme}://${_url.pathname.replace("/webapp/", "")}${
      _url.search
    }${_url.hash}`;
  } else if (
    url &&
    Platform.OS === "web" &&
    url.startsWith("http") &&
    url.includes("webapp")
  ) {
    const _url = new URL(url);
    return _url.pathname + _url.search + _url.hash;
  }
  return url;
};

const includesSafe = <T>(arr: T[] | undefined, value: T) =>
  Array.isArray(arr) && arr.includes(value);

const filterDataByPriceMode = (menuOptionsObj: any, priceMode: number) => {
  return Object.values(menuOptionsObj)
    .filter((option: any) => includesSafe(option.price_modes, priceMode))
    .sort((a: any, b: any) => a?.order - b?.order);
};

const arraysEqual = (a: any[], b: any[]) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return a.every((val, idx) => val === b[idx]);
};

const isPair = (item: any) => {
  return (
    item?.pair &&
    ["pair", "yes", "y", "1", "ear"].includes(item?.pair.toLowerCase())
  );
};

const isLinkedPair = (item: any) => {
  return (
    isPair(item) &&
    item?.is_linked_pair_parent === true &&
    item?.children?.length > 0
  );
};

const isParcel = (item: any) => {
  return (
    item?.diamond_category &&
    (item?.diamond_category === SearchFilterTypeEnumLowercase.PARCEL ||
      item?.diamond_category === SearchFilterTypeEnumLowercase.LOOSE_PARCEL)
  );
};

const isLooseParcel = (item: any) => {
  return (
    item?.diamond_category &&
    item?.diamond_category === SearchFilterTypeEnumLowercase.LOOSE_PARCEL
  );
};

const isMeleeItem = (item: any): boolean =>
  !!item?.sub_type?.toLowerCase().startsWith("melee") ||
  !!item?.product_sub_type?.toLowerCase().startsWith("melee");

const notNull = (v: any) => v !== undefined && v !== null;

const customParamsSerializer = (params: Record<string, any>) => {
  return Object.entries(params)
    .filter(([, value]) => notNull(value))
    .map(([key, value]) => {
      let encodedValue;

      if (typeof value === "object") {
        // Encode objects and arrays as JSON
        encodedValue = encodeURIComponent(JSON.stringify(value));
      } else {
        // Encode primitive values
        encodedValue = encodeURIComponent(String(value));
      }

      return `${encodeURIComponent(key)}=${encodedValue}`;
    })
    .join("&");
};

const preLoginRoutes = [
  Routes.Authentication,
  Routes.OnboardingWeb,
  Routes.ExternalHomeScreen,
  Routes.Register,
  Routes.PasswordUpdate,
  Routes.VerifiedScreen,
  Routes.ForgotPassword,
  Routes.DocumentUpload,
  Routes.ApplicationStatus,
  Routes.Onboarding,
  Routes.TermsAndConditions,
];

const isThreeDEnabled = async (itemDetail: any) => {
  return !!(
    itemDetail?.three_d_enabled &&
    itemDetail?.three_d_media &&
    itemDetail?.three_d_media?.length
  );
};

const handleEmailClick = async (t: any, emailText?: string) => {
  if (emailText && emailText !== "-") {
    const emails = emailText
      .split(";")
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    const mailtoURL =
      emails.length > 1 ? `mailto:${emails.join(",")}` : `mailto:${emails[0]}`;

    try {
      const canOpenMail = await Linking.canOpenURL(mailtoURL);
      if (canOpenMail) {
        await Linking.openURL(mailtoURL);
        return;
      }
    } catch (error) {}

    await Clipboard.setStringAsync(emailText);
    showToast(
      t("global.titleText.success"),
      t("global.titleText.emailCopied"),
      ToastTypes.SUCCESS,
      true,
      true
    );
  }
};

const getFormattedStockNumber = (
  stock: string | undefined,
  isCustomApp: boolean,
  isVdbMarketPlace: boolean,
  requestType: string | undefined
) => {
  if (!stock) return "";
  if (
    !isCustomApp &&
    isVdbMarketPlace &&
    requestType !== "retail_buy_request"
  ) {
    return "VDB-" + stock;
  }
  return stock;
};

const isGreaterVersion = (v1, v2) => {
  const a = v1?.split(".").map(Number);
  const b = v2?.split(".").map(Number);

  for (let i = 0; i < Math.max(a?.length, b?.length); i++) {
    const x = a?.[i] || 0;
    const y = b?.[i] || 0;

    if (x > y) return true;
    if (x < y) return false;
  }
  return false;
};

const addThreeDAdditionalSettingsTypes = {
  engrave: "engrave",
  morph: "morph",
  all: "all",
  headScale: "headScale",
};

export {
  isDiamondOrLabGrown,
  isGemstone,
  isJewelry,
  isDiamond,
  isLabGrown,
  isLabGrownJewelry,
  getSubtitlePriceModes,
  getButtonCategory,
  getFilterDetailData,
  isValidDate,
  getScreenContent,
  extractParamsFromURL,
  handleSupplierForPaUser,
  getBoxShadow,
  handleAdLinkPress,
  getNavigatorFromRoute,
  saveHistoryToSessionStorage,
  getSessionHistory,
  saveToSessionhistory,
  debounceFunction,
  getGMTTime,
  PERSISTENCE_KEY,
  resetAdBannerTime,
  getNormalizeRequestType,
  replaceDeeplinkScheme,
  filterDataByPriceMode,
  arraysEqual,
  isPair,
  isLinkedPair,
  customParamsSerializer,
  isParcel,
  preLoginRoutes,
  isLooseParcel,
  isMeleeItem,
  isThreeDEnabled,
  handleEmailClick,
  getFormattedStockNumber,
  isGreaterVersion,
  addThreeDAdditionalSettingsTypes,
};
